import React, { useState, useEffect } from 'react';

// Helper to convert MM/DD/YYYY to YYYY-MM-DD
const toInputDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string' || !/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return '';
  const [month, day, year] = dateStr.split('/');
  return `${year}-${month}-${day}`;
};

// Helper to convert YYYY-MM-DD to MM/DD/YYYY
const toDisplayDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const [year, month, day] = dateStr.split('-');
  return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
};

const EditableField = ({ label, value, onSave, fieldName, unit, type = 'text' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showCopied, setShowCopied] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setInputValue(type === 'date' ? toInputDate(value) : value);
    }
  }, [isEditing, value, type]);

  const handleSave = () => {
    const valueToSave = type === 'date' ? toDisplayDate(inputValue) : inputValue;
    if (valueToSave !== value) {
      onSave(fieldName, valueToSave);
    }
  };

  const handleBlur = () => {
    handleSave();
    setIsEditing(false);
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleCopy = async (e) => {
    e.stopPropagation(); // Prevent triggering edit mode
    try {
      await navigator.clipboard.writeText(value || '');
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000); // Hide after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };
  
  if (isEditing) {
    return (
      <div className="flex justify-between items-center py-1">
        <span className="text-sm font-medium text-gray-500 w-1/3">{label}</span>
        <div className="relative w-2/3">
          <input
            type={type}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            autoFocus
            className="text-sm text-gray-900 w-full p-1 bg-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {unit && type !== 'date' && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">{unit}</span>}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex justify-between items-center py-1 group cursor-pointer"
      onClick={() => setIsEditing(true)}
    >
      <span className="text-sm font-medium text-gray-500 w-1/3">{label}</span>
      <div className="flex items-center justify-between w-2/3">
        <span className="text-sm text-gray-900 p-1 rounded-md group-hover:bg-gray-100 transition-colors flex-1">
          {value} {unit && <span className="text-gray-500">{unit}</span>}
        </span>
        <button
          onClick={handleCopy}
          className={`ml-2 p-1 rounded transition-all duration-200 ${
            showCopied 
              ? 'bg-green-100 text-green-600' 
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
          title={showCopied ? 'Copied!' : 'Copy to clipboard'}
        >
          {showCopied ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
};

export default EditableField; 